/**
 * State of a pe3d base's editor: the variant list and folders, the selected
 * variant, its release (modules/pe3dRelease.js) and the unsaved edits.
 *
 * Inert on every other base (baseID null), so home.js can call it next to the
 * other hooks unconditionally.
 *
 * Edits are saved on their own, shortly after the last change, but only while
 * the variant is valid for its release — the server would refuse anything else,
 * and an invalid draft stays on screen with its problems instead. A save names
 * the revision it was made from; when someone else saved in between, the
 * server answers 409 with their version and this keeps the draft and says so,
 * rather than overwriting them.
 *
 * Languages (modules/pe3dLocalization.js): `language` is the one being edited
 * and previewed. The panels read `view` — the variant as that language
 * resolves — and write through `editView`; on "en", or on a release without
 * languages, those are the draft and `editDraft`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import * as api from "../modules/pe3dApi";
import { openRelease } from "../modules/pe3dRelease";
import * as l10n from "../modules/pe3dLocalization";

const SAVE_DELAY_MS = 900;

const errorText = (result) => {
  const error = result && result.error;
  if (!error) return `HTTP ${result && result.status}`;
  if (typeof error === "string") return error;
  if (Array.isArray(error.problems)) return `${error.error}: ${error.problems.join("; ")}`;
  return error.error || JSON.stringify(error);
};

export default function usePe3dEditor({ baseID, createSnackbar }) {
  const [variants, setVariants] = useState([]);
  const [tree, setTree] = useState([]);
  // The games the listed variants are of, with their releases (variant/find).
  const [games, setGames] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedID, setSelectedID] = useState(null);
  const [doc, setDoc] = useState(null); // the stored pe3dVariant
  const [draft, setDraft] = useState(null); // its `variant`, as being edited
  const [release, setRelease] = useState(null);
  const [releaseError, setReleaseError] = useState(null);
  const [saveState, setSaveState] = useState("saved"); // saved | pending | saving | invalid | conflict | error
  const [problems, setProblems] = useState([]);
  const [language, setLanguage] = useState(l10n.BASE_LANGUAGE);

  const saveTimer = useRef(null);
  const latest = useRef({ doc: null, draft: null });
  latest.current = { doc, draft };

  const notify = useCallback((message, variant = "error") => createSnackbar && createSnackbar(message, variant, false), [createSnackbar]);

  const refresh = useCallback(async () => {
    if (!baseID) return;
    setIsLoading(true);
    const result = await api.findVariants(baseID);
    setIsLoading(false);
    if (!result.ok) {
      notify(`Could not load the variants: ${errorText(result)}`);
      return;
    }
    setVariants(result.data.variants || []);
    setTree(result.data.tree || []);
    setGames(result.data.games || []);
  }, [baseID, notify]);

  useEffect(() => {
    setVariants([]);
    setTree([]);
    setGames([]);
    setSelectedID(null);
    setDoc(null);
    setDraft(null);
    setRelease(null);
    if (baseID) refresh();
  }, [baseID]);

  // The selected variant's release, opened in the browser for the preview and the form.
  useEffect(() => {
    let cancelled = false;
    setRelease(null);
    setReleaseError(null);
    if (!doc) return undefined;
    openRelease(doc.game, doc.release)
      .then((opened) => !cancelled && setRelease(opened))
      .catch((err) => !cancelled && setReleaseError(err.message));
    return () => {
      cancelled = true;
    };
  }, [doc && doc.game, doc && doc.release]);

  // What is wrong with the draft, by the release's own rules plus the server's
  // checks on what the engine's validator leaves alone (store links), and what
  // the release's checker says about each replaced file (read on the server).
  const [fileProblems, setFileProblems] = useState([]);
  const assetsKey = JSON.stringify(l10n.assetRefsOf(draft));
  useEffect(() => {
    let cancelled = false;
    setFileProblems([]);
    if (!release || !draft) return undefined;
    Promise.all(
      l10n.assetRefsOf(draft).map(({ slot, ref, language: lang }) =>
        release
          .describeSlot(slot, ref)
          .then((info) => info.errors || [])
          .catch((err) => [`assets.${slot}: ${err.message}`])
          .then((errors) => (lang ? errors.map((error) => `[${lang}] ${error}`) : errors))
      )
    ).then((lists) => !cancelled && setFileProblems([].concat(...lists)));
    return () => {
      cancelled = true;
    };
  }, [release, assetsKey]);
  useEffect(() => {
    if (!release || !draft) return setProblems([]);
    const badLinks = (links, prefix) =>
      Object.entries(links || {})
        .filter(([, link]) => link && !/^https?:\/\//.test(link))
        .map(([platform]) => `${prefix}storeLinks.${platform}: must be an http(s) link`);
    const linkProblems = [
      ...badLinks(draft.storeLinks, ""),
      ...Object.entries(draft.localizationOverrides || {}).flatMap(([lang, override]) => badLinks(override && override.storeLinks, `[${lang}] `)),
    ];
    return setProblems([...release.code.validateVariant(release.bundle.schema, draft), ...linkProblems, ...fileProblems]);
  }, [release, draft, fileProblems]);

  const save = useCallback(async () => {
    clearTimeout(saveTimer.current);
    const { doc: current, draft: edited } = latest.current;
    if (!current || !edited || _.isEqual(current.variant || {}, edited)) {
      setSaveState("saved");
      return true;
    }
    setSaveState("saving");
    const result = await api.updateVariant({ variantID: current._id, variant: JSON.stringify(edited), revision: current.revision || 0 });
    if (result.ok) {
      const stored = result.data.variant;
      setDoc(stored);
      // Keep edits typed while the save was in flight.
      if (_.isEqual(latest.current.draft, edited)) setSaveState("saved");
      else setSaveState("pending");
      return true;
    }
    if (result.status === 409) {
      setSaveState("conflict");
      notify("Someone else saved this variant meanwhile. Reload it to see their version; your edits are still on screen.", "warning");
      return false;
    }
    if (result.status === 422) {
      setSaveState("invalid");
      return false;
    }
    setSaveState("error");
    notify(`Could not save: ${errorText(result)}`);
    return false;
  }, [notify]);

  // Debounced save while valid.
  useEffect(() => {
    if (!doc || !draft) return;
    if (saveState === "conflict") return;
    // An edit that lands back on the stored value leaves nothing to save.
    if (_.isEqual(doc.variant || {}, draft)) {
      clearTimeout(saveTimer.current);
      if (saveState !== "saving") setSaveState("saved");
      return;
    }
    if (problems.length) {
      // A file check that lands after the edit must also stop a save already queued.
      clearTimeout(saveTimer.current);
      setSaveState("invalid");
      return;
    }
    setSaveState("pending");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(save, SAVE_DELAY_MS);
  }, [draft, problems]);

  const select = useCallback(async (variantID) => {
    if (latest.current.doc && String(latest.current.doc._id) === String(variantID)) return;
    if (saveTimer.current) await save();
    setSelectedID(variantID);
    setDoc(null);
    setDraft(null);
    if (!variantID) return;
    const result = await api.findVariant(variantID);
    if (!result.ok) {
      notify(`Could not open the variant: ${errorText(result)}`);
      return;
    }
    setDoc(result.data.variant);
    setDraft(_.cloneDeep(result.data.variant.variant || {}));
    setLanguage(l10n.BASE_LANGUAGE);
    setSaveState("saved");
  }, [save, notify]);

  /** Reload the selected variant from the server, dropping local edits. */
  const reload = useCallback(async () => {
    const id = latest.current.doc && latest.current.doc._id;
    if (!id) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = null;
    setDoc(null);
    const result = await api.findVariant(id);
    if (result.ok) {
      setDoc(result.data.variant);
      setDraft(_.cloneDeep(result.data.variant.variant || {}));
      setSaveState("saved");
    }
  }, []);

  const editDraft = useCallback((update) => {
    setDraft((current) => update(_.cloneDeep(current || {})));
    setSaveState((state) => (state === "conflict" ? state : "pending"));
  }, []);

  // The language being edited; a release without languages (or a language taken off) is "en".
  const localizationSupported = l10n.supportsLocalization(release);
  const languages = localizationSupported ? l10n.variantLanguages(draft) : [l10n.BASE_LANGUAGE];
  const activeLanguage = languages.includes(language) ? language : l10n.BASE_LANGUAGE;
  const view = useMemo(() => l10n.viewOf(release, draft, activeLanguage), [release, draft, activeLanguage]);

  const editView = useCallback(
    (update) => editDraft((current) => l10n.writeView(release, current, activeLanguage, update)),
    [editDraft, release, activeLanguage]
  );
  const isOverridden = useCallback((kind, key) => l10n.isOverridden(draft, activeLanguage, kind, key), [draft, activeLanguage]);
  const resetOverride = useCallback(
    (kind, key) => editDraft((current) => l10n.resetOverride(current, activeLanguage, kind, key)),
    [editDraft, activeLanguage]
  );
  const addLanguage = useCallback(
    (code) => {
      editDraft((current) => l10n.addLanguage(current, code));
      setLanguage(code);
    },
    [editDraft]
  );
  const removeLanguage = useCallback(
    (code) => {
      editDraft((current) => l10n.removeLanguage(current, code));
      setLanguage(l10n.BASE_LANGUAGE);
    },
    [editDraft]
  );

  const create = useCallback(async ({ title, game, release: version, startFrom, folderID, languages }) => {
    const result = await api.insertVariant({ baseID, title, game, release: version, startFrom: startFrom || null, folderID, languages });
    if (!result.ok) {
      notify(`Could not create the variant: ${errorText(result)}`);
      return null;
    }
    await refresh();
    await select(String(result.data.variant._id));
    return result.data.variant;
  }, [baseID, refresh, select, notify]);

  // Many at once, from the game's own variants; the dialog reports what was made and what was not.
  const importGameVariants = useCallback(async (params, callbacks) => {
    const result = await api.importGameVariants({ baseID, ...params }, callbacks);
    // Refreshed either way: a run that failed half way may still have made some.
    await refresh();
    return result;
  }, [baseID, refresh]);

  const duplicate = useCallback(async (variantID, title) => {
    const result = await api.duplicateVariant(variantID, title);
    if (!result.ok) return notify(`Could not duplicate: ${errorText(result)}`);
    await refresh();
    return select(String(result.data.variant._id));
  }, [refresh, select, notify]);

  const remove = useCallback(async (variantID) => {
    const result = await api.deleteVariant(variantID);
    if (!result.ok) return notify(`Could not delete: ${errorText(result)}`);
    if (String(selectedID) === String(variantID)) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
      setSelectedID(null);
      setDoc(null);
      setDraft(null);
    }
    return refresh();
  }, [selectedID, refresh, notify]);

  const updateMeta = useCallback(async (variantID, fields) => {
    const current = latest.current.doc;
    const isSelected = current && String(current._id) === String(variantID);
    // A title/language change bumps the revision, so pending edits go first.
    if (isSelected && saveTimer.current) await save();
    const revision = isSelected ? latest.current.doc.revision || 0 : undefined;
    const result = await api.updateVariant({ variantID, ...fields, ...(revision !== undefined && { revision }) });
    if (!result.ok) return notify(`Could not update: ${errorText(result)}`);
    if (isSelected) setDoc(result.data.variant);
    return refresh();
  }, [save, refresh, notify]);

  const changeRelease = useCallback(async (version) => {
    const current = latest.current.doc;
    if (!current) return false;
    if (saveTimer.current) await save();
    const result = await api.changeVariantRelease(current._id, version);
    if (!result.ok) {
      notify(`Could not move to ${version}: ${errorText(result)}`);
      return false;
    }
    setDoc(result.data.variant);
    setDraft(_.cloneDeep(result.data.variant.variant || {}));
    await refresh();
    return true;
  }, [save, refresh, notify]);

  const treeAction = useCallback(async (call) => {
    const result = await call();
    if (!result.ok) {
      notify(errorText(result));
      return false;
    }
    if (result.data && result.data.tree) setTree(result.data.tree);
    return true;
  }, [notify]);

  return {
    baseID,
    variants,
    tree,
    games,
    isLoading,
    selectedID,
    doc,
    draft,
    release,
    releaseError,
    problems,
    saveState,
    refresh,
    select,
    reload,
    save,
    editDraft,
    localizationSupported,
    languages,
    language: activeLanguage,
    setLanguage,
    view,
    editView,
    isOverridden,
    resetOverride,
    addLanguage,
    removeLanguage,
    create,
    importGameVariants,
    duplicate,
    remove,
    updateMeta,
    changeRelease,
    createFolder: (name, parentID) => treeAction(() => api.createFolder(baseID, name, parentID)),
    renameFolder: (folderID, name) => treeAction(() => api.renameFolder(baseID, folderID, name)),
    deleteFolder: (folderID) => treeAction(() => api.deleteFolder(baseID, folderID)),
    moveNode: (nodeID, parentID) => treeAction(() => api.moveNode(baseID, nodeID, parentID)),
  };
}
